/**
 * Hook for managing family plan membership and invitations
 *
 * Features:
 * - Fetch family plan details (tier, quota, member count)
 * - Check if user is family admin or member
 * - List family members
 * - Fetch pending invitations
 * - Invite new members
 * - Accept invitations
 * - Remove members (admin only)
 *
 * Data Sources:
 * - family_plans table (RLS: admin + members can view)
 * - family_invites table (RLS: admin can manage, invited user can view pending)
 * - profiles table (family_plan_id, is_family_admin)
 * - RPC functions: get_user_family_plan, get_family_plan_members
 *
 * Integration:
 * - React Query for caching and automatic refetching
 * - AuthContext for user authentication
 * - Supabase Edge Functions for mutations
 *
 * Migration status: DTO contract (2026-04-18, Wave 4B)
 * ────────────────────────────────────────────────
 * Read queries + all mutations (inviteMember, acceptInvite, removeMember,
 * cancelInvite) use `fetchJsonValidated(<Dto>, ...)` so server responses
 * are validated against shared Zod contracts. Backend/DB renames can no
 * longer drift the UI silently.
 */
import { useAuth } from "../contexts/AuthContext";
import {
	canAccessStrictAuthEndpoints,
	getAuthCapability,
} from "../lib/auth-capability";
import { logNetworkError } from "../lib/logNetworkError";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import {
	fetchJsonValidated,
	getAuthFailureReason,
} from "@dramadetector/api-client";
import {
	FamilyPlanDto,
	FamilyInviteResultDto,
	FamilyAcceptResultDto,
	FamilyRemoveResultDto,
	FamilyCancelInviteResultDto,
} from "@dramadetector/shared";
import { getEdgeFunctionUrl, getAuthHeaders } from "../lib/api-url";

/**
 * Public snake_case interfaces consumed across the app.
 *
 * The wire contract (FamilyPlanDto) is camelCase — the hook maps it to the
 * legacy snake_case shape so downstream components stay untouched during the
 * DTO migration. When those components are later modernized, this mapping
 * layer can be dropped.
 */
export interface FamilyPlan {
	id: string;
	tier: "drama_plus" | "dramax";
	billing_interval: "monthly" | "yearly";
	status: "active" | "cancelled" | "expired" | "past_due";
	admin_user_id: string;
	is_admin: boolean;
	member_count: number;
	max_members: number;
	daily_quota: number;
	current_period_end: string | null;
}

export interface FamilyMember {
	user_id: string;
	email: string;
	display_name: string | null;
	is_admin: boolean;
	joined_at: string;
}

export interface FamilyInvite {
	id: string;
	invited_email: string;
	invite_code: string;
	status: "pending" | "accepted" | "expired" | "cancelled";
	created_at: string;
	expires_at: string;
	deep_link: string;
	web_link: string;
}

type FamilyPlanResponseDto = z.infer<typeof FamilyPlanDto>;

export interface UseFamilyPlanReturn {
	// Family plan data
	familyPlan: FamilyPlan | null;
	isLoading: boolean;
	error: Error | null;

	// User role checks
	isFamilyMember: boolean;
	isFamilyAdmin: boolean;

	// Members management
	members: FamilyMember[];
	membersLoading: boolean;
	membersError: Error | null;

	// Invitations
	pendingInvites: FamilyInvite[];
	invitesLoading: boolean;
	invitesError: Error | null;

	// Mutations — return the DTO-validated payload. Callers may discard it.
	inviteMember: (email: string) => Promise<FamilyInvite>;
	acceptInvite: (
		inviteCode: string,
	) => Promise<z.infer<typeof FamilyAcceptResultDto>>;
	removeMember: (
		userId: string,
	) => Promise<z.infer<typeof FamilyRemoveResultDto>>;
	cancelInvite: (
		inviteId: string,
	) => Promise<z.infer<typeof FamilyCancelInviteResultDto>>;

	// Mutation states
	isInviting: boolean;
	isAccepting: boolean;
	isRemoving: boolean;
}

/** Max family members (mirrors subscription policy; not part of DTO). */
const FAMILY_MAX_MEMBERS = 5;

/** Map the DTO's `billing_interval` enum into the legacy pair used by UI code. */
function mapBillingInterval(
	value:
		| "weekly"
		| "monthly"
		| "quarterly"
		| "semester"
		| "yearly"
		| null
		| undefined,
): "monthly" | "yearly" {
	return value === "yearly" ? "yearly" : "monthly";
}

/**
 * Fetches family plan details, members, and invites in one call via get-family-plan handler.
 */
async function fetchFamilyPlanData(): Promise<{
	plan: FamilyPlan | null;
	members: FamilyMember[];
	invites: FamilyInvite[];
}> {
	const dto = await fetchJsonValidated(
		FamilyPlanDto,
		getEdgeFunctionUrl("get-family-plan"),
	);

	const plan: FamilyPlan | null = dto.plan
		? {
				id: dto.plan.familyPlanId,
				tier: dto.plan.tier,
				billing_interval: mapBillingInterval(dto.plan.billingInterval),
				status: dto.plan.status,
				admin_user_id: dto.plan.adminUserId,
				is_admin: dto.plan.isAdmin,
				member_count: dto.plan.memberCount,
				max_members: FAMILY_MAX_MEMBERS,
				daily_quota: dto.plan.dailyQuota,
				current_period_end: dto.plan.currentPeriodEnd,
			}
		: null;

	const members: FamilyMember[] = dto.members.map(
		(m: FamilyPlanResponseDto["members"][number]) => ({
			user_id: m.userId,
			email: m.email,
			display_name: m.displayName,
			is_admin: m.isAdmin,
			joined_at: m.joinedAt,
		}),
	);

	const invites: FamilyInvite[] = dto.invites.map(
		(invite: FamilyPlanResponseDto["invites"][number]) => ({
			id: invite.id,
			invited_email: invite.email,
			invite_code: invite.inviteCode,
			status: invite.status,
			created_at: invite.createdAt,
			expires_at: invite.expiresAt,
			deep_link: `dramadetector://family/accept?code=${invite.inviteCode}`,
			web_link: `https://dramadetector.com/family/accept?code=${invite.inviteCode}`,
		}),
	);

	return { plan, members, invites };
}

/**
 * Hook for family plan management
 */
export function useFamilyPlan(): UseFamilyPlanReturn {
	const { user, accessToken, loading: authLoading, isRealUser } = useAuth();
	const queryClient = useQueryClient();
	const authCapability = getAuthCapability({
		loading: authLoading,
		isRealUser,
	});
	const canAccessStrictAuth = canAccessStrictAuthEndpoints({
		loading: authLoading,
		isRealUser,
	});

	// Fetch family plan details, members, and invites in a single call
	const {
		data: familyData,
		isLoading,
		error,
	} = useQuery({
		queryKey: ["family-plan", user?.id],
		queryFn: async () => {
			try {
				return await fetchFamilyPlanData();
			} catch (err) {
				logNetworkError(err, {
					hook: "useFamilyPlan",
					endpoint: "get-family-plan",
					isAuthenticated: authCapability === "authenticated",
					extra: {
						authCapability,
						authFailureReason: getAuthFailureReason(err) ?? undefined,
					},
				});
				throw err;
			}
		},
		enabled: canAccessStrictAuth && !!user?.id,
		staleTime: 1000 * 60 * 5, // 5 minutes
		gcTime: 1000 * 60 * 30, // 30 minutes
	});

	const familyPlan = familyData?.plan ?? null;
	const members = familyData?.members ?? [];
	const membersLoading = isLoading;
	const membersError = error;
	const pendingInvites = familyData?.invites ?? [];
	const invitesLoading = isLoading;
	const invitesError = error;

	// Invite member mutation — validated DTO (BA token via fetchJsonValidated)
	const inviteMemberMutation = useMutation({
		mutationFn: async (email: string): Promise<FamilyInvite> => {
			if (!canAccessStrictAuth || !accessToken)
				throw new Error("Not authenticated");

			const dto = await fetchJsonValidated(
				FamilyInviteResultDto,
				getEdgeFunctionUrl("family-invite"),
				{
					method: "POST",
					headers: { ...getAuthHeaders(accessToken) },
					body: JSON.stringify({ invitee_email: email }),
				},
			);

			return {
				id: dto.invite_id,
				invited_email: dto.invited_email,
				invite_code: dto.invite_code,
				status: "pending",
				created_at: new Date().toISOString(),
				expires_at: dto.expires_at,
				deep_link: dto.deep_link,
				web_link: dto.web_link,
			};
		},
		onSuccess: () => {
			// Refetch invites and family plan (member count may change)
			queryClient.invalidateQueries({ queryKey: ["family-invites"] });
			queryClient.invalidateQueries({ queryKey: ["family-plan"] });
		},
	});

	// Accept invite mutation — validated DTO
	const acceptInviteMutation = useMutation({
		mutationFn: async (inviteCode: string) => {
			if (!canAccessStrictAuth || !accessToken)
				throw new Error("Not authenticated");

			return await fetchJsonValidated(
				FamilyAcceptResultDto,
				getEdgeFunctionUrl("family-accept"),
				{
					method: "POST",
					headers: { ...getAuthHeaders(accessToken) },
					body: JSON.stringify({ invite_code: inviteCode }),
				},
			);
		},
		onSuccess: () => {
			// Refetch family plan (user is now a member)
			queryClient.invalidateQueries({ queryKey: ["family-plan"] });
			queryClient.invalidateQueries({ queryKey: ["user-stats"] }); // Tier may have changed
		},
	});

	// Remove member mutation (admin only) — validated DTO
	const removeMemberMutation = useMutation({
		mutationFn: async (userId: string) => {
			if (!canAccessStrictAuth || !accessToken)
				throw new Error("Not authenticated");

			return await fetchJsonValidated(
				FamilyRemoveResultDto,
				getEdgeFunctionUrl("family-remove"),
				{
					method: "POST",
					headers: { ...getAuthHeaders(accessToken) },
					body: JSON.stringify({ member_user_id: userId }),
				},
			);
		},
		onSuccess: () => {
			// Refetch members and family plan (member count changed)
			queryClient.invalidateQueries({ queryKey: ["family-members"] });
			queryClient.invalidateQueries({ queryKey: ["family-plan"] });
		},
	});

	// Cancel invite mutation (admin only) — validated DTO
	const cancelInviteMutation = useMutation({
		mutationFn: async (inviteId: string) => {
			if (!canAccessStrictAuth || !accessToken)
				throw new Error("Not authenticated");

			return await fetchJsonValidated(
				FamilyCancelInviteResultDto,
				getEdgeFunctionUrl("family-cancel-invite"),
				{
					method: "POST",
					headers: { ...getAuthHeaders(accessToken) },
					body: JSON.stringify({ invite_id: inviteId }),
				},
			);
		},
		onSuccess: () => {
			// Refetch invites
			queryClient.invalidateQueries({ queryKey: ["family-invites"] });
		},
	});

	return {
		// Family plan data
		familyPlan: familyPlan || null,
		isLoading,
		error: error as Error | null,

		// User role checks
		isFamilyMember: !!familyPlan,
		isFamilyAdmin: familyPlan?.is_admin || false,

		// Members management
		members,
		membersLoading,
		membersError: membersError as Error | null,

		// Invitations
		pendingInvites,
		invitesLoading,
		invitesError: invitesError as Error | null,

		// Mutations
		inviteMember: inviteMemberMutation.mutateAsync,
		acceptInvite: acceptInviteMutation.mutateAsync,
		removeMember: removeMemberMutation.mutateAsync,
		cancelInvite: cancelInviteMutation.mutateAsync,

		// Mutation states
		isInviting: inviteMemberMutation.isPending,
		isAccepting: acceptInviteMutation.isPending,
		isRemoving: removeMemberMutation.isPending,
	};
}
